import {useEffect, useState} from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Grid, Typography, Backdrop, Paper, Container} from '@mui/material';
import Box from '@mui/material/Box';
import {useLocation} from 'react-router-dom';
import styled from 'styled-components';

import ErrorComponent from '../../components/error/ErrorComponent';
import Button from '../../components/form/buttons/Button/Button';
import Link from '../../components/link/Link';
import CartPrices from '../../components/payment/products/CartPrices/CartPrices';
import ProductCard from '../../components/payment/products/single-product/ProductCard/ProductCard';
import UpdateSubscriptionModal from '../../components/payment/products/UpdateSubscriptionModal/UpdateSubscriptionModal';
import Spinner from '../../components/spinner/Spinner';
import PageTitle from '../../components/typography/PageTitle';
import {APP_ROUTES} from '../../const';
import usePayment from '../../hooks/providers/usePayment';
import useProjects from '../../hooks/providers/useProjects';
import paymentMeans from '../../img/payment-means.png';

const PaymentMeansImage = styled.img`
  max-height: 25px;
`;

// eslint-disable-next-line complexity
const Products = () => {
  const [loadingGetPaymentLink, setLoadingGetPaymentLink] = useState(false);
  const [loadingGetPaymentLinkHidden, setLoadingGetPaymentLinkHidden] = useState(true);
  const [errorGettingPaymentLink, setErrorGettingPaymentLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [updateSubscriptionConfirmationModalOpen, setUpdateSubscriptionConfirmationModalOpen] = useState(false);

  const paymentContext = usePayment();

  const {products, updateSubscription, hasMembership, isSubmittingSubscriptionForm: isSubmitting, setIsSubmittingSubscriptionForm: setIsSubmitting, getUserSubscriptionStatus} = paymentContext;
  const {projects, fetchProjects} = useProjects();
  const location = useLocation();

  // This hook fixes Safari's back-forward cache behavior.
  // Safari restores pages from memory instead of reloading them when navigating back,
  // which prevents React components from remounting, refetching data, or resetting state.
  // Listening to the 'pageshow' event lets us detect this case (event.persisted === true)
  // and force a reload so the app behaves consistently across browsers.
  useEffect(() => {
    const handlePageShow = async event => {
      if (event.persisted) {
        await getUserSubscriptionStatus(true);
        setIsSubmitting(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleUpdateSubscription = async () => {
    setIsSubmitting(true);
    const res = await updateSubscription();
    if (res.success) setUpdateSubscriptionConfirmationModalOpen(true);
    setIsSubmitting(false);
  };

  const redirectToStripePayment = async () => {
    setErrorGettingPaymentLink('');
    setLoadingGetPaymentLink(true);
    setLoadingGetPaymentLinkHidden(false);
    const baseUrl = window.location.origin;
    const result = await paymentContext.getPaymentLink(baseUrl);

    setLoadingGetPaymentLink(false);
    setTimeout(() => setLoadingGetPaymentLinkHidden(true), 1000);

    if (result.status === 200) window.location.href = result.data;
    else setErrorGettingPaymentLink(result.message);
  };

  const handleSubmit = async () => {
    if (!hasMembership) return redirectToStripePayment();

    setIsSubmitting(true);
    await handleUpdateSubscription();
    setIsSubmitting(false);
  };

  const renderHeader = () => (
    <Grid container alignItems="flex-start" sx={{mb: 1}}>
      <Link to={APP_ROUTES.profile}>
        <Button size="small" startIcon={<ArrowBackIcon />} variant="text">
          Retour à mon compte
        </Button>
      </Link>
      <Grid container alignItems="center" mb={4}>
        <Grid item flex={1} mt={2}>
          <PageTitle>Les abonnements</PageTitle>
        </Grid>
        <Box mt={2}>
          <Link to={APP_ROUTES.bills}>
            <Button variant="outlined" color="primary">
              Mes factures
            </Button>
          </Link>
        </Box>
      </Grid>
    </Grid>
  );

  const renderProductsSection = () => {
    if (loadingGetPaymentLink || !loadingGetPaymentLinkHidden) return null;

    const orderedProducts = products?.sort((a, b) => a.order - b.order);
    return (
      <Grid mt={3} mb={5} container justifyContent="center">
        {orderedProducts.map((p, index) => (
          <ProductCard product={p} index={index} key={p.id} />
        ))}
      </Grid>
    );
  };

  const renderPaymentSection = () => (
    <Container maxWidth="xl" sx={{p: 0, my: 3}}>
      <Grid container justifyContent="space-between" px={{xs: 1, sm: 0}}>
        <Grid item mb={{xs: 2, md: 0}}>
          <Grid container alignItems="center">
            <Typography fontSize={12}>Moyens de paiement pris en charge :</Typography>
            <Box mt={0.5} ml={2.5}>
              <PaymentMeansImage src={paymentMeans} alt="Moyens de paiements" />
            </Box>
          </Grid>
        </Grid>
        <Grid item maxWidth={{xs: '100%', md: 300}} width="100%">
          <CartPrices isSubmitting={isSubmitting} onSubmit={handleSubmit} />
        </Grid>
      </Grid>
    </Container>
  );

  useEffect(() => {
    (async () => {
      paymentContext.setGetProductsListErrorMessage('');
      setLoading(true);
      if (projects === null) await fetchProjects();
      setLoading(false);
      setLoadingGetPaymentLink(false);
    })();
  }, []);

  useEffect(() => setLoadingGetPaymentLink(false), [location.pathname]);

  if (paymentContext.isGuest || paymentContext.isPartner) {
    return null;
  }

  if (loading) return <Spinner text="Chargement des formules" isLoading />;

  if (errorGettingPaymentLink || paymentContext.getProductsListErrorMessage) {
    return <ErrorComponent message={errorGettingPaymentLink || paymentContext.getProductsListErrorMessage} href={APP_ROUTES.profile} />;
  }

  return (
    <Box>
      <Container maxWidth="xl" sx={{mt: 4, p: {xs: 0.5, sm: 2}}}>
        <Paper sx={{p: {xs: 1, sm: 3}, borderRadius: 5}} elevation={3}>
          {renderHeader()}
          {loadingGetPaymentLink || !loadingGetPaymentLinkHidden ? <Spinner text="Redirection vers la page de paiement" isLoading /> : null}
          {renderProductsSection()}

          {isSubmitting && (
            <Backdrop sx={{height: '100vh', position: 'fixed', opacity: '0.75 !important', background: 'white'}} open>
              <Spinner text="Mise à jour de votre abonnement" isLoading />
            </Backdrop>
          )}
        </Paper>

        {!loadingGetPaymentLink && loadingGetPaymentLinkHidden && renderPaymentSection()}
      </Container>

      <UpdateSubscriptionModal isOpen={updateSubscriptionConfirmationModalOpen} onClose={() => setUpdateSubscriptionConfirmationModalOpen(false)} />
    </Box>
  );
};

export default Products;
